import store from "@/vuex/store.js";
import { Notification } from "element-ui";
import { apiV2Host } from "@/feathers/feathers-client.js";

const UNEXPECTED_ERROR = {
  name: "Error",
  message: "An unexpected error occurred!",
  code: 500,
};

const HTTP_METHODS = {
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  PATCH: "PATCH",
  DELETE: "DELETE",
};

export default class ApiClient {
  constructor({ baseURL = apiV2Host, servicePath = "" } = {}) {
    this.baseURL = servicePath ? `${baseURL}/${servicePath}` : `${baseURL}`;
  }

  async _parseResponsePayload(res) {
    try {
      const contentType = res.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        return await res.json();
      }

      // You can handle other content-type as needed
      // It assumes blob data for now
      return await res.blob();
    } catch (err) {
      console.error("Failed to parse response payload!");
      console.error(err);
      return null;
    }
  }

  _buildFetchOptions({
    method,
    body,
    extraHeaders = {},
    isBodyAttached = true,
  }) {
    const token = store.state.auth.accessToken;
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...extraHeaders,
    };

    if (!isBodyAttached) {
      delete headers["Content-Type"];
    }

    const options = { method, headers };

    if (body === null) {
      return options;
    }

    if (body instanceof FormData) {
      delete options.headers["Content-Type"];
      options.body = body;
      return options;
    }

    options.body = JSON.stringify(body);
    return options;
  }

  async request(
    path,
    {
      method = HTTP_METHODS.GET,
      body = null,
      headers = {},
      successMessage = "",
      isBodyAttached = true,
    }
  ) {
    try {
      const url = this.baseURL + path;
      const options = this._buildFetchOptions({
        method,
        body,
        isBodyAttached,
        extraHeaders: headers,
      });

      const res = await fetch(url, options);

      const code = res.status;
      const payload = await this._parseResponsePayload(res);

      if (!res.ok) {
        const { name, validationErrors } = payload;

        const message = validationErrors
          ? validationErrors[0]
          : payload.message;

        Notification({
          message,
          title: name,
          type: "error",
        });

        return { success: false, code, error: { name, message, code } };
      }

      // Notification not required for some API Calls
      if (successMessage) {
        Notification({
          type: "success",
          title: "Success",
          message: successMessage,
        });
      }
      return { success: true, data: payload, code };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        error: UNEXPECTED_ERROR,
        code: UNEXPECTED_ERROR.code,
      };
    }
  }

  get({ path = "", headers, successMessage, isBodyAttached = false }) {
    return this.request(path, {
      method: HTTP_METHODS.GET,
      headers,
      isBodyAttached,
      successMessage,
    });
  }

  post({
    path = "",
    body,
    headers,
    isBodyAttached,
    successMessage = "Created Successfully!",
  }) {
    return this.request(path, {
      method: HTTP_METHODS.POST,
      body,
      headers,
      isBodyAttached,
      successMessage,
    });
  }

  put({ path, body, headers, isBodyAttached, successMessage }) {
    return this.request(path, {
      method: HTTP_METHODS.PUT,
      body,
      headers,
      isBodyAttached,
      successMessage,
    });
  }

  patch({
    path,
    body,
    headers,
    isBodyAttached,
    successMessage = "Edited Successfully!",
  }) {
    return this.request(path, {
      method: HTTP_METHODS.PATCH,
      body,
      headers,
      isBodyAttached,
      successMessage,
    });
  }

  delete({
    path,
    headers,
    isBodyAttached = false,
    successMessage = "Deleted Successfully!",
  }) {
    return this.request(path, {
      method: HTTP_METHODS.DELETE,
      headers,
      isBodyAttached,
      successMessage,
    });
  }
}
